import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Card, Group, Text, Title, Button, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, StatusBadge, Tabs, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { admin } from '@repletoryreach/sdk-api';
import { useIntegrations } from '../../../data/useIntegrations';
import { formatDateTime } from '../../../lib/format';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import './alerts.css';
import { RecipientsCard } from './RecipientsCard';

const MUTED = 'var(--rr-color-text-muted)';

const SEVERITY_OPTS: { value: string; labelKey: string }[] = [
  { value: 'info', labelKey: 'system.alerts.severity.info' },
  { value: 'warning', labelKey: 'status.warning' },
  { value: 'error', labelKey: 'status.error' },
  { value: 'critical', labelKey: 'system.alerts.severity.critical' }
];

const SEVERITY_COLOUR: Record<string, string> = {
  info: 'var(--rr-color-info)',
  warning: 'var(--rr-color-warning)',
  error: 'var(--rr-color-error)',
  critical: 'var(--rr-color-error)'
};

/**
 * The three delivery channels the settings service looks up, in the order it
 * looks them up. `provider` is the integration row's provider code; `labelKey`
 * is what a person calls it.
 */
const CHANNELS: { provider: string; labelKey: string }[] = [
  // Email and WhatsApp are not integration rows: their recipients are on this page (2 October 2026).
  { provider: 'pagerduty', labelKey: 'system.alerts.channel.pagerduty' },
  { provider: 'slack_alerts', labelKey: 'system.alerts.channel.slack' },
  { provider: 'linear_issues', labelKey: 'system.alerts.channel.linear' }
];

/** Details are `key=value` per line; a line without `=` produces nothing. */
function parseDetails(raw: string): { parsed: Record<string, unknown>; ignored: number } {
  const parsed: Record<string, unknown> = {};
  let ignored = 0;
  for (const line of raw.split('\n')) {
    if (!line.trim()) continue;
    const ix = line.indexOf('=');
    if (ix > 0) parsed[line.slice(0, ix).trim()] = line.slice(ix + 1).trim();
    else ignored++;
  }
  return { parsed, ignored };
}

function statusFor(s: admin.AlertChannelOutcome['status']): 'healthy' | 'warning' | 'down' | 'inactive' {
  switch (s) {
    case 'ok': return 'healthy';
    case 'error': return 'down';
    case 'skipped':
    case 'skipped_not_critical':
    case 'skipped_resolved':
    case 'not_configured':
    default: return 'inactive';
  }
}

/**
 * The message body `AlertChannels.DispatchSlack` builds. Reproduced here so
 * the preview is the payload rather than an artist's impression of it: an
 * operator about to page someone at 02:00 should be able to read the words
 * that will arrive before they arrive.
 */
function slackText(summary: string, severity: string, source: string, details: Record<string, unknown>): string {
  const emoji = severity === 'critical' || severity === 'error'
    ? ':rotating_light:' // i18n-ignore: Slack emoji code, wire format
    : severity === 'warning'
      ? ':warning:' // i18n-ignore: Slack emoji code, wire format
      : ':information_source:'; // i18n-ignore: Slack emoji code, wire format
  const lines = Object.entries(details).map(([k, v]) => `• *${k}*: ${String(v)}`);
  return [`${emoji} *${summary}*`, `_Source:_ ${source}`, ...lines].join('\n'); // i18n-ignore: reproduces the Slack payload built by the settings service
}

/** The PagerDuty Events v2 body, minus the routing key, which is a secret. */
function pagerDutyPayload(summary: string, severity: string, source: string, details: Record<string, unknown>) {
  return {
    routing_key: '••••••••', // i18n-ignore: the real routing key is a credential and is never shown
    event_action: 'trigger', // i18n-ignore: PagerDuty Events v2 wire value
    payload: { summary, source, severity, custom_details: details }
  };
}

/** The Linear issue the dispatcher opens: title, description and priority. */
function linearPayload(summary: string, severity: string, source: string, details: Record<string, unknown>) {
  const priority = severity === 'critical' ? 1 : severity === 'error' ? 2 : severity === 'warning' ? 3 : 4;
  const body = Object.entries(details).map(([k, v]) => `- **${k}**: ${String(v)}`).join('\n');
  return {
    title: summary,
    priority,
    description: `Source: ${source}\nSeverity: ${severity}\n\n${body}` // i18n-ignore: reproduces the Linear description built by the settings service
  };
}

interface SentEntry {
  at: string;
  kind: 'dispatch' | 'test';
  summary: string;
  severity: string;
  channels: admin.AlertChannelOutcome[];
}

export default function AlertsPage() {
  const t = useT();
  const integrationsQ = useIntegrations();

  const [summary, setSummary] = useState(() => t('system.alerts.defaults.summary'));
  const [severity, setSeverity] = useState<'info' | 'warning' | 'error' | 'critical'>('warning');
  const [source, setSource] = useState('admin-ui');
  const [details, setDetails] = useState(() => t('system.alerts.defaults.details'));
  const [busy, setBusy] = useState(false);
  // Nothing stores what this page has sent, so the list is what this browser
  // tab has watched leave. It says so, rather than posing as a dispatch log.
  const [sent, setSent] = useState<SentEntry[]>([]);

  const { parsed, ignored } = useMemo(() => parseDetails(details), [details]);
  const summaryMissing = summary.trim().length === 0;

  // Which channels are actually enabled, so the operator knows who this will
  // reach before clicking rather than after.
  const enabledChannels = useMemo(() => {
    const rows = integrationsQ.data?.data ?? [];
    return CHANNELS.map((c) => ({
      ...c,
      enabled: rows.some((r) => r.provider_code === c.provider && r.enabled)
    }));
  }, [integrationsQ.data]);

  const liveChannels = enabledChannels.filter((c) => c.enabled);
  const liveLabel = liveChannels.length
    ? liveChannels.map((c) => t(c.labelKey)).join(', ')
    : t('system.alerts.confirm.noChannels');

  const previewSource = source.trim() || 'repletoryreach';
  const previewSummary = summary.trim() || t('system.alerts.preview.noSummary');

  async function send(kind: 'dispatch' | 'test') {
    setBusy(true);
    try {
      const res = kind === 'test'
        ? await admin.testAlert()
        : await admin.dispatchAlert({ summary, severity, source, details: parsed });
      setSent((prev) => [{
        at: new Date().toISOString(),
        kind,
        summary: kind === 'test' ? t('system.alerts.testPayloadSummary') : summary,
        severity: kind === 'test' ? 'info' : severity,
        channels: res.data.channels
      }, ...prev].slice(0, 10));
      const okChannels = res.data.channels.filter((c) => c.status === 'ok').map((c) => c.channel);
      if (okChannels.length) {
        if (kind === 'test') {
          notifySuccess(t('system.alerts.toast.testFired', { channels: okChannels.join(' + ') }), t('system.alerts.toast.testTitle'));
        } else {
          notifySuccess(t('system.alerts.toast.dispatched', { channels: okChannels.join(' + ') }), t('system.alerts.toast.dispatchedTitle'));
        }
      } else {
        notifyError(
          kind === 'test' ? t('system.alerts.toast.noChannelTest') : t('system.alerts.toast.noChannelDispatch'),
          t('settings.integrations.toast.nothingDispatched')
        );
      }
    } catch (e) {
      notifyError(
        e instanceof Error ? e.message : t('common.unknownError'),
        kind === 'test' ? t('system.alerts.toast.testFailedTitle') : t('system.alerts.toast.dispatchFailed')
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Both buttons reach a person outside the console, and the PagerDuty call is
   * `event_action: "trigger"`, which opens an incident that nothing here can
   * close again. So both confirm, both name the channels that will actually
   * fire, and neither claims to be a rehearsal.
   */
  function confirmSend(kind: 'dispatch' | 'test') {
    const isTest = kind === 'test';
    openConfirmModal({
      title: isTest ? t('system.alerts.confirm.test.title') : t('system.alerts.confirm.dispatch.title'),
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">
            {isTest
              ? t('system.alerts.confirm.test.body', { channels: liveLabel })
              : t('system.alerts.confirm.dispatch.body', { channels: liveLabel })}
          </Text>
          <Card withBorder>
            <Stack gap="xs">
              <Text role="caption" c={MUTED}>{t('system.alerts.confirm.payload')}</Text>
              <Text role="bodySmall" fw={600}>
                {isTest ? t('system.alerts.testPayloadSummary') : summary}
              </Text>
              <Text role="caption" c={MUTED}>
                {t('system.alerts.confirm.severity', {
                  severity: isTest ? t('system.alerts.severity.info') : t(SEVERITY_OPTS.find((o) => o.value === severity)?.labelKey ?? '')
                })}
              </Text>
            </Stack>
          </Card>
          <Text role="caption" c="var(--rr-color-warning)">{t('system.alerts.confirm.pagerDutyWarning')}</Text>
        </Stack>
      ),
      labels: {
        confirm: isTest ? t('system.alerts.confirm.test.confirm') : t('system.alerts.confirm.dispatch.confirm'),
        cancel: t('action.cancel')
      },
      confirmProps: { color: 'red' },
      onConfirm: () => { void send(kind); }
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('system.title')}
        title={t('system.alerts.title')}
        description={t('system.alerts.description')}
      />

      <Alert color="info">
        {t('system.alerts.banner')} <Link to="/settings/integrations">{t('system.alerts.configureLink')}</Link>
      </Alert>

      <RecipientsCard />

      <div className="rr-alerts-grid">
        <Card withBorder>
          <Stack gap="md">
            <Stack gap="xs">
              <Title order={5}>{t('system.alerts.section.compose')}</Title>
              <Text role="caption" c={MUTED}>{t('system.alerts.section.composeHint')}</Text>
            </Stack>
            <TextInput
              label={t('system.alerts.form.summary')}
              value={summary}
              onChange={(e) => setSummary(e.currentTarget.value)}
              description={t('system.alerts.form.summaryHint')}
              error={summaryMissing ? t('system.alerts.form.summaryRequired') : undefined}
              required
            />
            <Group gap="md" grow align="flex-start">
              <Select
                label={t('system.alerts.form.severity')}
                value={severity}
                onChange={(e) => setSeverity(e.currentTarget.value as typeof severity)}
                data={SEVERITY_OPTS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
              />
              <TextInput
                label={t('system.alerts.form.source')}
                value={source}
                onChange={(e) => setSource(e.currentTarget.value)}
                description={t('system.alerts.form.sourceHint')}
              />
            </Group>
            <Textarea
              label={t('system.alerts.form.details')}
              value={details}
              onChange={(e) => setDetails(e.currentTarget.value)}
              description={
                ignored > 0
                  ? t('system.alerts.form.detailsIgnored', { count: ignored })
                  : t('system.alerts.form.detailsHint')
              }
              minRows={4}
            />

            {/* Routing is decided by the dispatcher from the Integrations
                registry, not chosen here, so this reports which rows are live
                instead of offering a per-channel choice the endpoint would
                ignore. */}
            <Stack gap="xs">
              <Text role="bodySmall" fw={600}>{t('system.alerts.section.routing')}</Text>
              <Group gap="sm" wrap="wrap">
                {enabledChannels.map((c) => (
                  <StatusBadge key={c.provider} status={c.enabled ? 'healthy' : 'inactive'} size="xs">
                    {c.enabled
                      ? t('system.alerts.channelEnabled', { channel: t(c.labelKey) })
                      : t('system.alerts.channelDisabled', { channel: t(c.labelKey) })}
                  </StatusBadge>
                ))}
              </Group>
              <Text role="caption" c={MUTED}>{t('system.alerts.section.routingHint')}</Text>
            </Stack>

            <Stack gap="xs">
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={() => confirmSend('test')} loading={busy}>
                  {t('system.alerts.sendTestPage')}
                </Button>
                <Button color="red" onClick={() => confirmSend('dispatch')} loading={busy} disabled={summaryMissing}>
                  {t('system.alerts.dispatch')}
                </Button>
              </Group>
              <Text role="caption" c={MUTED} ta="right">{t('system.alerts.buttonsCaption')}</Text>
            </Stack>
          </Stack>
        </Card>

        {/* What each channel will receive, rendered from the form as you type.
            Nothing here calls anything. */}
        <Card withBorder>
          <Stack gap="md">
            <Stack gap="xs">
              <Title order={5}>{t('system.alerts.preview.title')}</Title>
              <Text role="caption" c={MUTED}>{t('system.alerts.preview.note')}</Text>
            </Stack>
            <Tabs defaultValue="slack">
              <Tabs.List>
                <Tabs.Tab value="slack">{t('system.alerts.channel.slack')}</Tabs.Tab>
                <Tabs.Tab value="pagerduty">{t('system.alerts.preview.pagerdutyTab')}</Tabs.Tab>
                <Tabs.Tab value="linear">{t('system.alerts.preview.linearTab')}</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="slack">
                <Stack gap="sm">
                  <div
                    className="rr-alert-preview"
                    style={{ ['--rr-alert-accent' as string]: SEVERITY_COLOUR[severity] }}
                  >
                    <p className="rr-alert-preview__text">
                      {slackText(previewSummary, severity, previewSource, parsed)}
                    </p>
                  </div>
                  <Text role="caption" c={MUTED}>{t('system.alerts.preview.slackHint')}</Text>
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="pagerduty">
                <Stack gap="sm">
                  <pre className="rr-alert-json">
                    {JSON.stringify(pagerDutyPayload(previewSummary, severity, previewSource, parsed), null, 2)}
                  </pre>
                  <Text role="caption" c={MUTED}>{t('system.alerts.preview.pagerdutyHint')}</Text>
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="linear">
                <Stack gap="sm">
                  <pre className="rr-alert-json">
                    {JSON.stringify(linearPayload(previewSummary, severity, previewSource, parsed), null, 2)}
                  </pre>
                  <Text role="caption" c={MUTED}>{t('system.alerts.preview.linearHint')}</Text>
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        </Card>

        <Stack gap="md">
          <Card withBorder>
            <Stack gap="md">
              <Stack gap="xs">
                <Title order={5}>{t('system.alerts.history.title')}</Title>
                <Text role="caption" c={MUTED}>{t('system.alerts.history.hint')}</Text>
              </Stack>
              {sent.length === 0 ? (
                <EmptyState title={t('system.alerts.history.empty')} description={t('system.alerts.history.emptyHint')} />
              ) : (
                <div className="rr-alert-history">
                  {sent.map((entry) => (
                    <div key={entry.at} className="rr-alert-history__row">
                      <Stack gap="xs">
                        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                          <Text role="bodySmall" fw={600}>{entry.summary}</Text>
                          <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(entry.at)}
                          </Text>
                        </Group>
                        <Text role="caption" c={MUTED}>
                          {t('system.alerts.confirm.severity', { severity: entry.severity })}
                          {entry.kind === 'test' ? ` · ${t('system.alerts.history.testPage')}` : ''}
                        </Text>
                        <Group gap="xs" wrap="wrap">
                          {entry.channels.map((c) => (
                            <Group key={c.channel} gap="xs" wrap="nowrap">
                              <Badge size="xs" variant="light" color="gray">{c.channel === 'email' || c.channel === 'whatsapp' ? t(`system.alerts.channel.${c.channel}`) : c.channel}</Badge>
                              <StatusBadge status={statusFor(c.status)} size="xs">{outcomeLabel(t, c.status)}</StatusBadge>
                              {c.message && <Text role="caption" c={MUTED}>{c.message}</Text>}
                            </Group>
                          ))}
                        </Group>
                      </Stack>
                    </div>
                  ))}
                </div>
              )}
            </Stack>
          </Card>

          {/* Named rather than mocked: the rules that fire on their own and the
              silences that mute them live in Alertmanager, and the console has
              no read path to either. */}
          <Card withBorder>
            <Stack gap="xs">
              <Title order={5}>{t('system.alerts.rules.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('system.alerts.rules.body')}</Text>
            </Stack>
          </Card>
        </Stack>
      </div>
    </Stack>
  );
}

function outcomeLabel(t: Translate, status: admin.AlertChannelOutcome['status']): string {
  switch (status) {
    case 'ok': return t('status.sent');
    case 'error': return t('status.failed');
    case 'skipped': return t('status.skipped');
    case 'not_configured': return t('status.notConfigured');
    case 'skipped_not_critical':
    case 'skipped_resolved': return t('status.skipped');
    default: return t('status.unknown');
  }
}
